import { Inject, Injectable } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { concatLatestFrom } from '@ngrx/operators';
import { BehaviorSubject, EMPTY, from, Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';

import {
  MicroLayoutStore,
  RoiStore,
  FindingStore,
  RoiChangeStore,
  RoiStatsStore,
  SYNC_FINDINGS_USE_CASE,
} from '../../stores';
import { SyncRoiChangesUseCase, SyncFindingsUseCase } from '@analysis/use-cases';
import { SYNC_CHANGES_USE_CASE } from '../roi-sync';
import { SnackbarService } from '@shared/ui';

export type MicroSyncPayload = {
  sampleId: string;
  currentAnalystId: string;
  selectedAssetId: string;
};

const SNACKBAR_DEBOUNCE_MS = 300;

@Injectable()
export class MicroSyncEffects {
  private snackbarRef: MatSnackBarRef<unknown> | undefined;
  private readonly snackbarTrigger$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly roiStatsStore: RoiStatsStore,
    private readonly findingStore: FindingStore,
    private readonly microLayout: MicroLayoutStore,
    @Inject(SYNC_CHANGES_USE_CASE)
    private readonly syncChangesUseCase: SyncRoiChangesUseCase,
    @Inject(SYNC_FINDINGS_USE_CASE)
    private readonly syncFindingsUseCase: SyncFindingsUseCase,
    private readonly snackbarService: SnackbarService
  ) {
    this.wireSnackbar(this.snackbarTrigger$);
  }

  readonly syncChanges = this.roiStore.effect((payload$: Observable<MicroSyncPayload>) =>
    payload$.pipe(
      tap(() => {
        this.roiChangeStore.setSyncing(true);
        this.findingStore.setSyncingChanges(true);
        this.snackbarTrigger$.next(true);
      }),
      concatLatestFrom(() => [this.findingStore.findings$, this.findingStore.changedFindings$]),
      switchMap(([payload, findingsEntities, changedFindings]) => {
        const { sampleId, currentAnalystId, selectedAssetId } = payload;
        const findingsList = Object.values(findingsEntities);

        return from(this.syncChangesUseCase.execute({ sampleId, currentAnalystId })).pipe(
          tap(({ syncedRois, deletedRois, counterDeltas }) => {
            const roisToUpsert = syncedRois.filter((roi) => roi.assetId === selectedAssetId);
            this.roiStore.completeSyncWithSave({
              roisToUpsert,
              deletedRois: deletedRois.map((r) => r.id),
              allUpdatedRois: syncedRois,
            });
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            this.roiChangeStore.clearAllChanges();
            const analyzedAssetIds = Array.from(
              new Set(syncedRois.map((r) => r.assetId).filter((id): id is string => !!id))
            );
            this.microLayout.markAssetsAsAnalyzed(analyzedAssetIds);
          }),
          switchMap(() =>
            from(
              this.syncFindingsUseCase.execute({
                sampleId,
                findings: findingsList,
                changedFindings,
              })
            )
          ),
          tap((savedFindings) => {
            this.findingStore.updateFindingsAfterSync(savedFindings);
            const analyzedFindingAssetIds = Array.from(
              new Set(savedFindings.map((f) => f.assetId).filter((id): id is string => !!id))
            );
            this.microLayout.markAssetsAsAnalyzed(analyzedFindingAssetIds);
          }),
          tap(() => this.snackbarService.show('analysis.saved')),
          map(() => undefined),
          catchError((error) => {
            this.roiChangeStore.setError(error.message);
            this.findingStore.setSyncError(error.message);
            return EMPTY;
          }),
          finalize(() => this.snackbarTrigger$.next(false))
        );
      })
    )
  );

  private readonly wireSnackbar = this.roiStore.effect((flag$: Observable<boolean>) =>
    flag$.pipe(
      distinctUntilChanged(),
      switchMap((active) => (active ? timer(SNACKBAR_DEBOUNCE_MS).pipe(map(() => true)) : of(false))),
      tap((active: boolean) => {
        this.snackbarRef?.dismiss();
        this.snackbarRef = undefined;
        if (!active) return;
        this.snackbarRef = this.snackbarService.show('analysis.saving_changes', {
          duration: 0,
          dismissible: false,
        });
      })
    )
  );
}
